import AccessDenied from "components/AccessDenied";
import AppShell from "components/AppShell";
import Dashboard from "components/Dashboard";
import PageNotFound from "components/NotFound";
import Report from "components/Report";
import configData from "config/config.json";
import React, { useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { PermittedRoles } from "utils/permissions";
import EditClub from "./pages/admin/ClubEditor";
import ClubsList from "./pages/admin/ClubsList";
import PlayerCardEditor from "./pages/admin/PlayerCardEditor";
import PlayerCards from "./pages/admin/PlayerCardsList";
import EditPosition from "./pages/admin/PositionEditor";
import PositionsList from "./pages/admin/PositionsList";
import AdminCredentials from "./pages/adminCredentials/AdminCredentials";
import AllUsers from "./pages/allUsers/AllUsers";
import UserDetails from "./pages/allUsers/UserDetails";
import HomeBannerEditor from "./pages/banners/HomeBannerEditor";
import HomeBanners from "./pages/banners/HomeBanners";
import BrandEditor from "./pages/brand/BrandEditor";
import Brands from "./pages/brand/Brands";
import CardBackground from "./pages/cardBackground/CardBackground";
import CardBackgroundEditor from "./pages/cardBackground/CardBackgroundEditor";
import Configurations from "./pages/configurations/Configurations";
import Currency from "./pages/currency/Currency";
import CurrencyEditor from "./pages/currency/CurrencyEditor";
import DailyRewardCycles from "./pages/dailyRewardCycles/DailyRewardCycles";
import DailyRewardCycleEditor from "./pages/dailyRewardCycles/DailyRewardCycleEditor";
import EOSRewardConfigEditor from "./pages/eosRewardConfigs/EOSRewardConfigEditor";
import EOSRewardConfigs from "./pages/eosRewardConfigs/EOSRewardConfigs";
import InitiateEOS from "./pages/eosRewardConfigs/InitiateEOS";
import FatigueConfig from "./pages/fatigueConfig/FatigueConfig";
import FTUEPackManagement from "./pages/FTUEPackManagement/FTUEPackManagement";
import LeagueEditor from "./pages/leagues/LeagueEditor";
import Leagues from "./pages/leagues/Leagues";
import Logos from "./pages/logos/Logos";
import MasterGameEditor from "./pages/masterGame/MasterGameEditor";
import MasterGameList from "./pages/masterGame/MasterGameList";
import NotificationEditor from "./pages/notifications/NotificationEditor";
import Notifications from "./pages/notifications/Notifications";
import BaseMarketEditor from "./pages/operator/BaseMarketEditor";
import OperatorEditor from "./pages/operator/OperatorEditor";
import Operators from "./pages/operator/Operators";
import OperatorGamesEditor from "./pages/operatorGames/OperatorGamesEditor";
import OperatorGamesList from "./pages/operatorGames/OperatorGamesList";
import PackCategories from "./pages/packCategories/PackCategories";
import PackCategoryEditor from "./pages/packCategories/PackCategoryEditor";
import PackEditor from "./pages/packs/PackEditor";
import PackList from "./pages/packs/PackList";
import BulkUploadPacks from "./pages/packs/BulkUploadPacks";
import PackImages from "./pages/packs/PackImages";
import PlatformEditor from "./pages/platform/PlatformEditor";
import Platforms from "./pages/platform/Platforms";
import PBFPlayers from "./pages/players/PBFPlayerList";
import TFGPlayers from "./pages/players/TFGPlayerList";
import TFGPlayerUpdate from "./pages/players/TFGPlayerUpdate";
import QuickBetEditor from "./pages/quickBet/QuickBetEditor";
import QuickBetList from "./pages/quickBet/QuickBetList";
import ReCalculateTeamRatings from "./pages/reCalculateTeamRatings/reCalculateTeamRatings";
import DailyCoinReport from "./pages/reports/DailyCoinReport";
import DailyReport from "./pages/reports/DailyReport";
import MarketReport from "./pages/reports/MarketReport";
import PlayerCoinReport from "./pages/reports/PlayerCoinReport";
import PlayerReport from "./pages/reports/PlayerReport";
import RoundDetails from "./pages/reports/RoundDetails";
import TransactionReport from "./pages/reports/TransactionReport";
import SellPlayerEditor from "./pages/sellPlayer/SellPlayerEditor";
import SplashScreen from "./pages/splashScreen/SplashScreen";
import SplashScreenEditor from "./pages/splashScreen/SplashScreenEditor";
import Challenges from "./pages/challenges/Challenges";
import ChallengesEditor from "./pages/challenges/ChallengesEditor";
import BulkUploadChallenges from "./pages/challenges/BulkUploadChallenges";
import ChallengePrizeWheels from "./pages/challenges/ChallengePrizeWheels";
import ChallengeRewardImages from "./pages/challenges/ChallengeRewardImages";
import ChallengeRewardImageEditor from "./pages/challenges/ChallengeRewardImageEditor";
import TeamBadges from "./pages/badges/TeamBadges";
import ObjectiveTypeTitles from "./pages/objectiveTypeTitles/ObjectiveTypeTitles";
import ObjectiveTypeTitleEditor from "./pages/objectiveTypeTitles/ObjectiveTypeTitleEditor";
import Store from "./pages/store/Store";
import StoreEditor from "./pages/store/StoreEditor";
import StoreBannerEditor from "./pages/storeBanners/StoreBannerEditor";
import StoreBanners from "./pages/storeBanners/StoreBanners";
import BotGhostTeamManagement from "./pages/teamManagement/BotGhostTeamManagement";
import Translation from "./pages/translation/Translation";
import UserEditor from "./pages/userManagement/UserEditor";
import UserManagement from "./pages/userManagement/UserManagement";
import PortalUsers from "./pages/portalUser/PortalUsers";
import PortalUserEditor from "./pages/portalUser/PortalUserEditor";
import ReportSchedulers from "./pages/reportScheduler/ReportSchedulers";
import ReportSchedulerEditor from "./pages/reportScheduler/ReportSchedulerEditor";
import {
  VaultRewardConfigEditor,
  VaultRewardConfigList,
} from "./pages/vaultRewardConfig";
import WeeklyLeaderboard from "./pages/weeklyLeaderboard/WeeklyLeaderboard";
import WeeklyLeaderboardConfig from "./pages/weeklyLeaderboardConfig/WeeklyLeaderboardConfig";
import QueueMonitoring from "./pages/queueMonitoring/QueueMonitoring";
import QueueJobs from "./pages/queueMonitoring/QueueJobs";
import Tutorials from "./pages/tutorials/TutorialsPage";
import TutorialEditor from "./pages/tutorials/TutorialEditor";
import { PrivateRoute } from "./routes";
import OperatorLogoEditor from "./pages/operatorLogos/OperatorLogoEditor";
import OperatorLogos from "./pages/operatorLogos/OperatorLogos";

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location?.pathname === "/") {
      navigate(configData.routes.dashboard);
    }
  }, [location, navigate]);

  return (
    <AppShell>
      <Routes location={location}>
        <Route
          path={configData.routes.players.PBFPlayers}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYERS.PBF_PLAYERS_LIST}
            >
              <PBFPlayers />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.players.TFGPlayers}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYERS.TFG_PLAYERS_LIST}
            >
              <TFGPlayers />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.players.TFGPlayerUpdate}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYERS.TFG_PLAYER_UPDATE}
            >
              <TFGPlayerUpdate />
            </PrivateRoute>
          }
        />
        {/* Positions routes */}
        <Route
          path={configData.routes.playerDetails.positions}
          element={
            <PrivateRoute requiredRole={PermittedRoles.POSITION.LIST_ROLES}>
              <PositionsList />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.playerDetails.createPosition}
          element={
            <PrivateRoute requiredRole={PermittedRoles.POSITION.CREATE_ROLES}>
              <EditPosition />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.playerDetails.editPosition}
          element={
            <PrivateRoute requiredRole={PermittedRoles.POSITION.UPDATE_ROLES}>
              <EditPosition />
            </PrivateRoute>
          }
        />
        {/* Clubs routes */}
        <Route
          path={configData.routes.playerDetails.clubs}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CLUB.LIST_ROLES}>
              <ClubsList />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.playerDetails.createClub}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CLUB.CREATE_ROLES}>
              <EditClub />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.playerDetails.editClub}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CLUB.UPDATE_ROLES}>
              <EditClub />
            </PrivateRoute>
          }
        />
        {/* PlayerCard routes */}
        <Route
          path={configData.routes.playerDetails.playerCards}
          element={
            <PrivateRoute requiredRole={PermittedRoles.PLAYER.CARD.LIST_ROLES}>
              <PlayerCards />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.playerDetails.createPlayerCard}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYER.CARD.CREATE_ROLES}
            >
              <PlayerCardEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.playerDetails.editPlayerCard}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYER.CARD.UPDATE_ROLES}
            >
              <PlayerCardEditor />
            </PrivateRoute>
          }
        />
        {/* Packs routes */}
        <Route
          path={configData.routes.packs.packs}
          element={
            <PrivateRoute requiredRole={PermittedRoles.PLAYER.PACK.LIST_ROLES}>
              <PackList />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.packs.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYER.PACK.CREATE_ROLES}
            >
              <PackEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.packs.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYER.PACK.UPDATE_ROLES}
            >
              <PackEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.packs.packImages}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYER.PACK.CREATE_ROLES}
            >
              <PackImages />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.packs.bulkUploadPacks}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PLAYER.PACK.CREATE_ROLES}
            >
              <BulkUploadPacks />
            </PrivateRoute>
          }
        />
        {/* Leagues routes */}
        <Route
          path={configData.routes.leagues.leagues}
          element={
            <PrivateRoute requiredRole={PermittedRoles.LEAGUE.LIST_ROLES}>
              <Leagues />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.leagues.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.LEAGUE.CREATE_ROLES}>
              <LeagueEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.leagues.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.LEAGUE.UPDATE_ROLES}>
              <LeagueEditor />
            </PrivateRoute>
          }
        />
        {/* Platforms routes */}
        <Route
          path={configData.routes.platforms.platforms}
          element={
            <PrivateRoute requiredRole={PermittedRoles.PLATFORM.LIST_ROLES}>
              <Platforms />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.platforms.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.PLATFORM.CREATE_ROLES}>
              <PlatformEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.platforms.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.PLATFORM.UPDATE_ROLES}>
              <PlatformEditor />
            </PrivateRoute>
          }
        />
        {/* Store Routes */}
        <Route
          path={configData.routes.store.store}
          element={
            <PrivateRoute requiredRole={PermittedRoles.STORE.LIST_ROLES}>
              <Store />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.store.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.STORE.CREATE_ROLES}>
              <StoreEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.store.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.STORE.UPDATE_ROLES}>
              <StoreEditor />
            </PrivateRoute>
          }
        />
        {/* Store Banners Routes */}
        <Route
          path={configData.routes.storeBanners.storeBanners}
          element={
            <PrivateRoute requiredRole={PermittedRoles.STORE.LIST_ROLES}>
              <StoreBanners />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.storeBanners.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.STORE.CREATE_ROLES}>
              <StoreBannerEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.storeBanners.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.STORE.UPDATE_ROLES}>
              <StoreBannerEditor />
            </PrivateRoute>
          }
        />
        {/* Operators routes */}
        <Route
          path={configData.routes.operators.operators}
          element={
            <PrivateRoute requiredRole={PermittedRoles.OPERATOR.LIST_ROLES}>
              <Operators />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.operators.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.OPERATOR.CREATE_ROLES}>
              <OperatorEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.operators.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.OPERATOR.UPDATE_ROLES}>
              <OperatorEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.operators.editBaseMarkets}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OPERATOR.UPDATE_BASE_MARKET}
            >
              <BaseMarketEditor />
            </PrivateRoute>
          }
        />
        {/* Brands routes */}
        <Route
          path={configData.routes.brands.brands}
          element={
            <PrivateRoute requiredRole={PermittedRoles.BRAND.LIST_ROLES}>
              <Brands />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.brands.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.BRAND.CREATE_ROLES}>
              <BrandEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.brands.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.BRAND.UPDATE_ROLES}>
              <BrandEditor />
            </PrivateRoute>
          }
        />
        {/* Reports routes */}
        <Route
          path={configData.routes.reports.report}
          element={
            <PrivateRoute requiredRole={PermittedRoles.REPORT.INFO_ROLES}>
              <Report />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.reports.dailyReport}
          element={
            <PrivateRoute requiredRole={PermittedRoles.REPORT.INFO_ROLES}>
              <DailyReport />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.reports.playerReport}
          element={
            <PrivateRoute requiredRole={PermittedRoles.REPORT.INFO_ROLES}>
              <PlayerReport />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.reports.transactionReport}
          element={
            <PrivateRoute requiredRole={PermittedRoles.REPORT.INFO_ROLES}>
              <TransactionReport />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.reports.marketReport}
          element={
            <PrivateRoute requiredRole={PermittedRoles.REPORT.INFO_ROLES}>
              <MarketReport />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.reports.dailyCoinReport}
          element={
            <PrivateRoute requiredRole={PermittedRoles.REPORT.INFO_ROLES}>
              <DailyCoinReport />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.reports.playerCoinReport}
          element={
            <PrivateRoute requiredRole={PermittedRoles.REPORT.INFO_ROLES}>
              <PlayerCoinReport />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.reports.roundDetails}
          element={
            <PrivateRoute requiredRole={PermittedRoles.REPORT.INFO_ROLES}>
              <RoundDetails />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.dashboard}
          element={
            <PrivateRoute requiredRole={PermittedRoles.DASHBOARD.INFO_ROLES}>
              <Dashboard />
            </PrivateRoute>
          }
        />
        {/* currency routes */}
        <Route
          path={configData.routes.currency.currency}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CURRENCY.LIST_ROLES}>
              <Currency />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.currency.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CURRENCY.CREATE_ROLES}>
              <CurrencyEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.currency.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CURRENCY.UPDATE_ROLES}>
              <CurrencyEditor />
            </PrivateRoute>
          }
        />
        {/* Banners routes */}
        <Route
          path={configData.routes.homeBanners.homeBanners}
          element={
            <PrivateRoute requiredRole={PermittedRoles.BANNERS.LIST_ROLES}>
              <HomeBanners />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.homeBanners.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.BANNERS.CREATE_ROLES}>
              <HomeBannerEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.homeBanners.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.BANNERS.UPDATE_ROLES}>
              <HomeBannerEditor />
            </PrivateRoute>
          }
        />
        {/* Splash Screen routes */}
        <Route
          path={configData.routes.splashScreen.splashScreen}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.SPLASH_SCREEN.LIST_ROLES}
            >
              <SplashScreen />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.splashScreen.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.SPLASH_SCREEN.CREATE_ROLES}
            >
              <SplashScreenEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.splashScreen.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.SPLASH_SCREEN.UPDATE_ROLES}
            >
              <SplashScreenEditor />
            </PrivateRoute>
          }
        />
        {/* Challenges routes */}
        <Route
          path={configData.routes.challenges.challenges}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CHALLENGES.LIST_ROLES}>
              <Challenges />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.createChallenges}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CHALLENGES.CREATE_ROLES}>
              <ChallengesEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.editChallenges}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CHALLENGES.UPDATE_ROLES}>
              <ChallengesEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.bulkUploadChallenges}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CHALLENGES.CREATE_ROLES}>
              <BulkUploadChallenges />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.rewardImages}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.CHALLENGE_REWARD_IMAGES.LIST_ROLES}
            >
              <ChallengeRewardImages />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.createRewardImages}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.CHALLENGE_REWARD_IMAGES.CREATE_ROLES}
            >
              <ChallengeRewardImageEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.editRewardImages}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.CHALLENGE_REWARD_IMAGES.UPDATE_ROLES}
            >
              <ChallengeRewardImageEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.prizeWheels}
          element={
            <PrivateRoute requiredRole={PermittedRoles.CHALLENGES.LIST_ROLES}>
              <ChallengePrizeWheels />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.badges.teamBadges}
          element={
            <PrivateRoute requiredRole={PermittedRoles.BADGES.LIST_ROLES}>
              <TeamBadges />
            </PrivateRoute>
          }
        />
        {/* Objective Type Titles routes */}
        <Route
          path={configData.routes.challenges.objectiveTypeTitles}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OBJECTIVE_TYPE_TITLES.LIST_ROLES}
            >
              <ObjectiveTypeTitles />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.createObjectiveTypeTitles}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OBJECTIVE_TYPE_TITLES.CREATE_ROLES}
            >
              <ObjectiveTypeTitleEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.challenges.editObjectiveTypeTitles}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OBJECTIVE_TYPE_TITLES.UPDATE_ROLES}
            >
              <ObjectiveTypeTitleEditor />
            </PrivateRoute>
          }
        />
        {/* Daily Reward Cycle routes */}
        <Route
          path={configData.routes.dailyRewardCycles.list}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.DAILYREWARDCYCLES.LIST_ROLES}
            >
              <DailyRewardCycles />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.dailyRewardCycles.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.DAILYREWARDCYCLES.CREATE_ROLES}
            >
              <DailyRewardCycleEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.dailyRewardCycles.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.DAILYREWARDCYCLES.UPDATE_ROLES}
            >
              <DailyRewardCycleEditor />
            </PrivateRoute>
          }
        />

        <Route
          path={configData.routes.translation.translation}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.TRANSLATION.CREATE_ROLES}
            >
              <Translation />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.cardBackground.cardBackground}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.CARD_BACKGROUND.LIST_ROLES}
            >
              <CardBackground />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.cardBackground.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.TRANSLATION.CREATE_ROLES}
            >
              <CardBackgroundEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.cardBackground.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.TRANSLATION.CREATE_ROLES}
            >
              <CardBackgroundEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={
            configData.routes.vaultRewardsAndDropRates.vaultRewardsAndDropRates
          }
          element={
            <PrivateRoute
              requiredRole={
                PermittedRoles.VAULT_REWARDS_AND_DROP_RATES.LIST_ROLES
              }
            >
              <VaultRewardConfigList />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.vaultRewardsAndDropRates.create}
          element={
            <PrivateRoute
              requiredRole={
                PermittedRoles.VAULT_REWARDS_AND_DROP_RATES.CREATE_ROLES
              }
            >
              <VaultRewardConfigEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.vaultRewardsAndDropRates.edit}
          element={
            <PrivateRoute
              requiredRole={
                PermittedRoles.VAULT_REWARDS_AND_DROP_RATES.UPDATE_ROLES
              }
            >
              <VaultRewardConfigEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.packCategory.packCategory}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PACK_CATEGORY.LIST_ROLES}
            >
              <PackCategories />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.packCategory.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PACK_CATEGORY.LIST_ROLES}
            >
              <PackCategoryEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.packCategory.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PACK_CATEGORY.LIST_ROLES}
            >
              <PackCategoryEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.notifications.notifications}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.NOTIFICATIONS.LIST_ROLES}
            >
              <Notifications />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.notifications.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.NOTIFICATIONS.CREATE_ROLES}
            >
              <NotificationEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.notifications.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.NOTIFICATIONS.UPDATE_ROLES}
            >
              <NotificationEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.sellPlayer.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.SELL_PLAYER.CREATE_ROLES}
            >
              <SellPlayerEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.quickBet.quickBet}
          element={
            <PrivateRoute requiredRole={PermittedRoles.QUICK_BET.LIST_ROLES}>
              <QuickBetList />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.quickBet.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.QUICK_BET.UPDATE_ROLES}>
              <QuickBetEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.quickBet.create}
          element={
            <PrivateRoute requiredRole={PermittedRoles.QUICK_BET.CREATE_ROLES}>
              <QuickBetEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.configurations}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.CONFIGURATIONS.UPDATE_ROLES}
            >
              <Configurations />
            </PrivateRoute>
          }
        />
        {/* Master Game routes */}
        <Route
          path={configData.routes.masterGame.list}
          element={
            <PrivateRoute requiredRole={PermittedRoles.MASTER_GAME.LIST_ROLES}>
              <MasterGameList />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.masterGame.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.MASTER_GAME.CREATE_ROLES}
            >
              <MasterGameEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.masterGame.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.MASTER_GAME.UPDATE_ROLES}
            >
              <MasterGameEditor />
            </PrivateRoute>
          }
        />
        {/* Operator Games routes */}
        <Route
          path={configData.routes.operatorGames.list}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OPERATOR_GAMES.LIST_ROLES}
            >
              <OperatorGamesList />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.operatorGames.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OPERATOR_GAMES.CREATE_ROLES}
            >
              <OperatorGamesEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.operatorGames.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OPERATOR_GAMES.UPDATE_ROLES}
            >
              <OperatorGamesEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.botghostTeamManagement}
          element={
            <PrivateRoute
              requiredRole={
                PermittedRoles.BOT_AND_GHOST_TEAM_MANAGEMENT.UPDATE_ROLES
              }
            >
              <BotGhostTeamManagement />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.logos}
          element={
            <PrivateRoute requiredRole={PermittedRoles.LOGOS.ALLOWED_ROLES}>
              <Logos />
            </PrivateRoute>
          }
        />
        {/* User Management routes */}
        <Route
          path={configData.routes.userManagement.list}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.USER_MANAGEMENT.LIST_ROLES}
            >
              <UserManagement />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.userManagement.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.USER_MANAGEMENT.CREATE_ROLES}
            >
              <UserEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.userManagement.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.USER_MANAGEMENT.UPDATE_ROLES}
            >
              <UserEditor />
            </PrivateRoute>
          }
        />
        {/* Portal Users routes */}
        <Route
          path={(configData.routes as any).portalUsers.list}
          element={
            <PrivateRoute requiredRole={PermittedRoles.PORTAL_USER.LIST_ROLES}>
              <PortalUsers />
            </PrivateRoute>
          }
        />
        <Route
          path={(configData.routes as any).portalUsers.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PORTAL_USER.CREATE_ROLES}
            >
              <PortalUserEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={(configData.routes as any).portalUsers.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.PORTAL_USER.UPDATE_ROLES}
            >
              <PortalUserEditor />
            </PrivateRoute>
          }
        />
        {/* Report Scheduler routes */}
        <Route
          path={(configData.routes as any).reportSchedulers.list}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.REPORT_SCHEDULER?.LIST_ROLES}
            >
              <ReportSchedulers />
            </PrivateRoute>
          }
        />
        <Route
          path={(configData.routes as any).reportSchedulers.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.REPORT_SCHEDULER?.CREATE_ROLES}
            >
              <ReportSchedulerEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={(configData.routes as any).reportSchedulers.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.REPORT_SCHEDULER?.UPDATE_ROLES}
            >
              <ReportSchedulerEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.adminCredentials}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.ADMIN_CREDENTIALS.ALLOWED_ROLES}
            >
              <AdminCredentials />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.accessDenied}
          element={<AccessDenied />}
        />
        <Route path="*" element={<PageNotFound />} />

        {/* EOS Reward Config routes */}
        <Route
          path={configData.routes.eosRewardConfig.eosRewardConfig}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.EOS_REWARD_CONFIG.LIST_ROLES}
            >
              <EOSRewardConfigs />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.eosRewardConfig.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.EOS_REWARD_CONFIG.CREATE_ROLES}
            >
              <EOSRewardConfigEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.eosRewardConfig.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.EOS_REWARD_CONFIG.UPDATE_ROLES}
            >
              <EOSRewardConfigEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.eosRewardConfig.initate}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.EOS_REWARD_CONFIG.CREATE_ROLES}
            >
              <InitiateEOS />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.allUsers.allUsers}
          element={
            <PrivateRoute requiredRole={PermittedRoles.ALL_USERS.ALLOWED_ROLES}>
              <AllUsers />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.allUsers.userInfo}
          element={
            <PrivateRoute requiredRole={PermittedRoles.ALL_USERS.ALLOWED_ROLES}>
              <UserDetails />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.ftuePackManagement}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.FTUE_PACK_MANAGEMENT.UPDATE_ROLES}
            >
              <FTUEPackManagement />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.reCalculateTeamRatings.allReCalc}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.RECALCULATE_TEAM_RATINGS.LIST_ROLES}
            >
              <ReCalculateTeamRatings />
            </PrivateRoute>
          }
        />
        {/* Fatigue Configuration route */}
        <Route
          path={configData.routes.fatigueConfig.fatigueConfig}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.FATIGUE_CONFIG.UPDATE_ROLES}
            >
              <FatigueConfig />
            </PrivateRoute>
          }
        />
        {/* Weekly Leaderboard routes */}
        <Route
          path={configData.routes.weeklyLeaderboard.view}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.WEEKLY_LEADERBOARD.VIEW_ROLES}
            >
              <WeeklyLeaderboard />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.weeklyLeaderboard.config}
          element={
            <PrivateRoute
              requiredRole={
                PermittedRoles.WEEKLY_LEADERBOARD_CONFIG.UPDATE_ROLES
              }
            >
              <WeeklyLeaderboardConfig />
            </PrivateRoute>
          }
        />
        {/* Queue Monitoring route */}
        <Route
          path={configData.routes.queueMonitoring.dashboard}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.QUEUE_MONITORING.VIEW_ROLES}
            >
              <QueueMonitoring />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.queueMonitoring.rgsQueue}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.QUEUE_MONITORING.VIEW_ROLES}
            >
              <QueueJobs queueType="rgs" />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.queueMonitoring.cmsQueue}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.QUEUE_MONITORING.VIEW_ROLES}
            >
              <QueueJobs queueType="cms" />
            </PrivateRoute>
          }
        />
        {/* Tutorial routes */}
        <Route
          path={configData.routes.tutorials.tutorials}
          element={
            <PrivateRoute requiredRole={PermittedRoles.TUTORIAL.LIST_ROLES}>
              <Tutorials />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.tutorials.edit}
          element={
            <PrivateRoute requiredRole={PermittedRoles.TUTORIAL.UPDATE_ROLES}>
              <TutorialEditor />
            </PrivateRoute>
          }
        />
        {/* Operator Logos routes */}
        <Route
          path={configData.routes.operatorLogos.operatorLogos}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OPERATOR_LOGO.LIST_ROLES}
            >
              <OperatorLogos />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.operatorLogos.create}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OPERATOR_LOGO.CREATE_ROLES}
            >
              <OperatorLogoEditor />
            </PrivateRoute>
          }
        />
        <Route
          path={configData.routes.operatorLogos.edit}
          element={
            <PrivateRoute
              requiredRole={PermittedRoles.OPERATOR_LOGO.UPDATE_ROLES}
            >
              <OperatorLogoEditor />
            </PrivateRoute>
          }
        />
      </Routes>
    </AppShell>
  );
};

export default Home;
